import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Container, Row, Col, Card, Badge, Button, Spinner, Alert } from "react-bootstrap";
import { apiGet } from "../services/BaseService";
import { IMAGE_BASE } from "../Config";
import { getProgramImageUrl, handleImageError } from "../utils/imageUtils";
import CommunicationButtons from "../components/CommunicationButtons";
import CustomerSupportWidget from "../components/CustomerSupportWidget";
import CommunicationService from "../services/CommunicationService";

const ProgramDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [program, setProgram] = useState<any>(null);
  const [festivalPlays, setFestivalPlays] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [schedules, setSchedules] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchProgramDetails();
    }
  }, [id]);

  const fetchProgramDetails = async () => {
    try {
      setLoading(true);
      
      // Use the new details API endpoint
      const data = await apiGet(`/programs/${id}/details`);
      setProgram(data.program);
      setFestivalPlays(data.festivalPlays || []);
      setGroups(data.groups || []);
      setSchedules(data.schedules || []);
      
    } catch (error) {
      setError("Failed to load program details");
    } finally {
      setLoading(false);
    }
  };

  const isTheaterFestival = (program: any) => {
    // You might want to fetch program types to check properly
    return program?.programType && program.title?.toLowerCase().includes('festival');
  };

  const formatDuration = (hours?: number, minutes?: number) => {
    if (!hours && !minutes) return "Duration TBA";
    const h = hours ? `${hours}h` : "";
    const m = minutes ? `${minutes}m` : "";
    return `${h} ${m}`.trim();
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="warning" />
        <p className="mt-3">Loading program details...</p>
      </Container>
    );
  }

  if (error || !program) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <h4>Program Not Found</h4>
          <p>{error || "The requested program could not be found."}</p>
          <Link to="/programs" className="btn btn-warning">Back to Programs</Link>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      {/* Breadcrumb Navigation */}
      <Row className="mb-3">
        <Col xs={12}>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/groups" className="text-decoration-none">Groups</Link>
              </li>
              {groups.length > 0 && (
                <li className="breadcrumb-item">
                  <Link to={`/group/${groups[0]._id}`} className="text-decoration-none">
                    {groups[0].name}
                  </Link>
                </li>
              )}
              <li className="breadcrumb-item active" aria-current="page">
                {program.title}
              </li>
            </ol>
          </nav>
        </Col>
      </Row>

      {/* Hero Section */}
      <Row className="mb-5">
        <Col lg={4} className="mb-4">
          <Card className="border-0 shadow-lg">
            <Card.Img 
              variant="top" 
              src={getProgramImageUrl(program.image1)}
              style={{ height: "300px", objectFit: "cover" }}
              onError={(e) => handleImageError(e, 'PROGRAM')}
            />
          </Card>
        </Col>
        <Col lg={8}>
          <h1 className="display-5 fw-bold text-dark mb-3">{program.title}</h1>
          <p className="lead text-muted mb-4">{program.description || 'No description available'}</p>
          
          <div className="d-flex flex-wrap gap-3 mb-4">
            <Link to={`/program/${id}/schedule`} className="btn btn-warning btn-lg">
              <i className="bi bi-calendar-check me-2"></i>
              Check Booking Schedules
            </Link>
            <Button
              variant="outline-success"
              size="lg"
              onClick={() => CommunicationService.shareProgram(
                program.title,
                groups[0]?.name || 'Theatre Group',
                'Theatre Program',
                formatDuration(program.durationHours, program.durationMinutes),
                program.description || 'Amazing theatre program',
                window.location.href,
                'whatsapp'
              )}
            >
              <i className="bi bi-share me-2"></i>
              Share Program
            </Button>
            {groups.length > 0 && (
              <CommunicationButtons
                variant="contact"
                size="lg"
                data={{
                  organizerName: groups[0].name,
                  organizerPhone: program.phone || groups[0].contactPhone || groups[0].phone || '',
                  organizerEmail: program.email || groups[0].email || '',
                  eventName: program.title,
                  eventDate: 'Check schedule for dates',
                  eventTime: formatDuration(program.durationHours, program.durationMinutes),
                  venue: 'Various venues',
                  customerName: 'Interested Customer',
                  customerPhone: '',
                  customerEmail: ''
                }}
              />
            )}
          </div>
          
          <Row className="g-3 mb-4">
            <Col md={6}>
              <div className="d-flex align-items-center mb-2">
                <i className="bi bi-code text-warning me-2"></i>
                <span><strong>Program Code:</strong> {program.programCode}</span>
              </div>
              <div className="d-flex align-items-center mb-2">
                <i className="bi bi-clock text-warning me-2"></i>
                <span><strong>Duration:</strong> {formatDuration(program.durationHours, program.durationMinutes)}</span>
              </div>
              {program.language && (
                <div className="d-flex align-items-center mb-2">
                  <i className="bi bi-translate text-warning me-2"></i>
                  <span><strong>Language:</strong> {program.language}</span>
                </div>
              )}
            </Col>
            <Col md={6}>
              {program.email && (
                <div className="d-flex align-items-center mb-2">
                  <i className="bi bi-envelope text-warning me-2"></i>
                  <span><strong>Email:</strong> {program.email}</span>
                </div>
              )}
              {program.phone && (
                <div className="d-flex align-items-center mb-2">
                  <i className="bi bi-telephone text-warning me-2"></i>
                  <span><strong>Phone:</strong> {program.phone}</span>
                </div>
              )}
            </Col>
          </Row>
        </Col>
      </Row>

      {/* Festival Plays Section */}
      {isTheaterFestival(program) && festivalPlays.length > 0 && (
        <Row className="mb-5">
          <Col xs={12}>
            <h3 className="fw-bold mb-4">
              <i className="bi bi-play-circle text-warning me-2"></i>
              Scheduled Plays ({festivalPlays.length})
            </h3>
            <Row className="g-4">
              {festivalPlays.map((festivalPlay) => (
                <Col key={festivalPlay._id} md={6} lg={4}>
                  <Card className="h-100 border-0 shadow-sm">
                    <Card.Body>
                      <Card.Title className="h6">{festivalPlay.playId.title}</Card.Title>
                      <div className="small text-muted mb-2">
                        <div><strong>Date:</strong> {new Date(festivalPlay.scheduledDate).toLocaleDateString()}</div>
                        <div><strong>Time:</strong> {festivalPlay.scheduledTime}</div>
                        {festivalPlay.venue && <div><strong>Venue:</strong> {festivalPlay.venue}</div>}
                      </div>
                      {festivalPlay.notes && (
                        <Card.Text className="small">{festivalPlay.notes}</Card.Text>
                      )}
                      <Link to={`/play/${festivalPlay.playId._id}`} className="btn btn-sm btn-warning">
                        View Play Details
                      </Link>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Col>
        </Row>
      )}

      {/* Associated Groups */}
      {groups.length > 0 && (
        <Row className="mb-5">
          <Col xs={12}>
            <h3 className="fw-bold mb-4">
              <i className="bi bi-people text-warning me-2"></i>
              Associated Groups ({groups.length})
            </h3>
            <Row className="g-3">
              {groups.map((group) => (
                <Col key={group._id} md={6} lg={4}>
                  <Card className="h-100 border-0 shadow-sm">
                    <Card.Body>
                      <Card.Title className="h6">{group.name || 'Unknown Group'}</Card.Title>
                      <Link to={`/group/${group._id}`} className="btn btn-sm btn-outline-warning">
                        View Group
                      </Link>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Col>
        </Row>
      )}

      {/* Schedules Section */}
      {schedules.length > 0 && (
        <Row className="mb-5">
          <Col xs={12}>
            <h3 className="fw-bold mb-4">
              <i className="bi bi-calendar-event text-warning me-2"></i>
              Schedules ({schedules.length})
            </h3>
            <Row className="g-3">
              {schedules.map((schedule, index) => (
                <Col key={index} md={6} lg={4}>
                  <Card className="h-100 border-0 shadow-sm">
                    <Card.Body>
                      <div className="mb-2">
                        <strong>{new Date(schedule.startDate).toLocaleDateString()}</strong>
                        {schedule.endDate && (
                          <span> - {new Date(schedule.endDate).toLocaleDateString()}</span>
                        )}
                      </div>
                      <div className="small text-muted mb-2">
                        <div><strong>Time:</strong> {schedule.timing}</div>
                        <div><strong>Address:</strong> {schedule.address}</div>
                        <div><strong>Pin Code:</strong> {schedule.pinCode}</div>
                        {schedule.organizerName && (
                          <div><strong>Organizer:</strong> {schedule.organizerName}</div>
                        )}
                        {schedule.organizerPhone && (
                          <div><strong>Contact:</strong> {schedule.organizerPhone}</div>
                        )}
                      </div>
                      <Badge bg="info">{schedule.state}</Badge>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Col>
        </Row>
      )}
      
      {/* Customer Support Widget */}
      {/* <CustomerSupportWidget position="bottom-right" /> */}
    </Container>
  );
};

export default ProgramDetailPage;